import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Typography,
  Box
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { clearCountUnitImages, fetchCountUnitImages } from "../../../redux/actions/unitImagesActions";
import { clearContract, fetchContract } from "../../../redux/actions/contractsAction";
import { fetchCountsDisabilityTypes } from "../../../redux/actions/a11yesActions";
import { clearAccessibilityUnitsCounts, fetchAccessibilityUnitsCounts } from "../../../redux/actions/unitsActions";
import {
  clearTotalAccessibilityProgressBar,
  totalAccessibilityProgressBar
} from "../../../redux/actions/propertyActions";
import {
  clearCompany,
  fetchCompaniesInLive, fetchCompany
} from "../../../redux/actions/companyActions";
import { fetchUser } from "../../../redux/actions/userActions";
import {
  clearMixpanelData,
  fetchActionPerVisitor,
  fetchInteractionWithTheAcc,
  fetchOpenedTheAccessibility
} from "../../../redux/actions/servicesActions";
import moment from "moment";
import {
  getAccessibilityUnitsCounts,
  getActionPerVisitor, getCompanies,
  getCountsDisabilityTypes,
  getInteractionWithTheAcc,
  getOpenedTheAccessibility,
  getTotalAccessibilityProgressBar,
  getUnitImagesCount,
  getUser,
  getUserProfile
} from "../../../selectors";
import { IUser } from "../../../models/User";
import { clearCountDisabilityTypes } from "../../../redux/actions-declarations/a11yes";
import TabsMetrics from "./components/TabsMetrics";
import BusinessMetrics from "./components/metrics/BusinessMetrics";
import ServiceMetrics from "./components/metrics/ServiceMetrics";
import CustomDateRangePicker from "./components/CustomDateRangePicker";
import SelectProperty from "./components/SelectProperty";
import ReloadButton from "./components/ReloadButton";

const FORMAT_DATA = 'YYYY-MM-DD'

const StatsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loadedOnce = useRef(false)
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [tab, setTab] = useState(0)
  const user = useSelector(getUser);
  const userProfile = useSelector<IUser | undefined>(getUserProfile);
  const countImages = useSelector(getUnitImagesCount)
  const countDisabilityTypes = useSelector<any>(getCountsDisabilityTypes)
  const countsAccessibility = useSelector<any>(getAccessibilityUnitsCounts)
  const totalAmountProperties = useSelector(getTotalAccessibilityProgressBar)
  const openedTheAccessibility = useSelector<any>(getOpenedTheAccessibility)
  const interactionWithTheAcc = useSelector<any>(getInteractionWithTheAcc)
  const actionPerVisitor = useSelector<any>(getActionPerVisitor)
  const companies = useSelector<any>(getCompanies)
  const [selectedDate, setSelectedDate] = React.useState<any>([null, null]);

  useEffect(() => {
    dispatch(fetchUser(user!.user, user!.token))
    return () => {
      if (selectedCompanyId.length > 0) {
        clearData()
      }
    }
  }, [])

  useEffect(() => {
    if (userProfile && !loadedOnce.current) {
      const companyId = userProfile.company
      dispatch(fetchCompany(companyId, user!.token));
      dispatch(fetchContract(companyId, user!.token))
      fetchDataByCompanyIdWithValidation(companyId)
      if (userProfile.is_staff) {
        dispatch(fetchCompaniesInLive(user!.token))
      }
      setSelectedCompanyId(companyId)
      loadedOnce.current = true
    }
  }, [userProfile]);

  const getDataRange = () => {
    const today = moment(new Date()).format(FORMAT_DATA)

    let date = {
      from: moment().subtract(7, 'days').format(FORMAT_DATA),
      to: moment(new Date()).format(FORMAT_DATA)
    }

    if (selectedDate[0] && selectedDate[1]) {
      date.from =moment(selectedDate[0]).format(FORMAT_DATA)
      date.to = moment(selectedDate[1]).format(FORMAT_DATA)
    }

    if (moment(today).isSame(date.to)) {
      date.to = moment(date.to)
        .subtract(1, 'days')
        .format(FORMAT_DATA)
    }

    return date
  }

  const fetchDataByCompanyIdWithValidation = (companyId: string) => {
    const date = getDataRange()
    if (!totalAmountProperties.notCompleted && !totalAmountProperties.completed) {
      dispatch(totalAccessibilityProgressBar(user!.token, companyId))
    }
    if (!countImages) {
      dispatch(fetchCountUnitImages(user!.token, companyId))
    }
    if (!countDisabilityTypes) {
      dispatch(fetchCountsDisabilityTypes(user!.token, companyId))
    }
    if (!countsAccessibility) {
      dispatch(fetchAccessibilityUnitsCounts(user!.token, companyId))
    }
    if (!openedTheAccessibility) {
      dispatch(fetchOpenedTheAccessibility(user!.token, date, companyId))
    }
    if (!interactionWithTheAcc) {
      dispatch(fetchInteractionWithTheAcc(user!.token, date, companyId))
    }
    if (!actionPerVisitor) {
      dispatch(fetchActionPerVisitor(user!.token, date, companyId))
    }
  }

  const fetchDataByCompanyId = (companyId: string) => {
    const date = getDataRange()
    dispatch(fetchCompany(companyId, user!.token));
    dispatch(fetchContract(companyId, user!.token));
    dispatch(totalAccessibilityProgressBar(user!.token, companyId))
    dispatch(fetchAccessibilityUnitsCounts(user!.token, companyId))
    dispatch(fetchCountUnitImages(user!.token, companyId))
    dispatch(fetchCountsDisabilityTypes(user!.token, companyId))
    dispatch(fetchOpenedTheAccessibility(user!.token, date, companyId))
    dispatch(fetchInteractionWithTheAcc(user!.token, date, companyId))
    dispatch(fetchActionPerVisitor(user!.token, date, companyId))
  }

  const clearData = async () => {
    dispatch(clearTotalAccessibilityProgressBar())
    dispatch(clearCountUnitImages())
    dispatch(clearCountDisabilityTypes())
    dispatch(clearAccessibilityUnitsCounts())
    dispatch(clearMixpanelData())
    dispatch(clearCompany())
    dispatch(clearContract())
  }

  const getMetric = () => {
    switch (tab) {
      case 0:
        return <BusinessMetrics selectedCompanyId={selectedCompanyId}/>
      case 1:
        return <ServiceMetrics />
      default:
        return <BusinessMetrics selectedCompanyId={selectedCompanyId}/>
    }
  }

  const handlerReload = () => {
    clearData().then(() => {
      fetchDataByCompanyId(selectedCompanyId)
    })
  }

  return (
    <Box p={12}>
      <Helmet title={t("routes_Stats")} />
      <Box display='flex' flexDirection='row' justifyContent='space-between' flexWrap='wrap' style={{minHeight: '43px'}}>
        <Box flexGrow='1' flexBasis='30%'>
          <Typography variant="h3" gutterBottom display="inline">
            {t('statsPage_Statistics_page')}
          </Typography>
        </Box>
        <Box display='flex' flexDirection='row' flexGrow='1' flexBasis='30%' justifyContent='flex-end'>
          {userProfile?.is_staff && companies ? (
            <Box width='200px'>
              <SelectProperty
                selectedProperty={selectedCompanyId}
                setSelectedProperty={ async (id) => {
                  setSelectedCompanyId(id);
                  await clearData()
                  fetchDataByCompanyId(id)
                }}
                listProperty={companies}
              />
            </Box>
          ) : null}
          {tab === 1 ? (
            <>
              <Box display='flex' justifyContent='flex-end'>
                <CustomDateRangePicker
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                />
              </Box>
              <Box display='flex' alignItems='center' style={{padding: 0}}>
                <ReloadButton onClick={handlerReload}/>
              </Box>
            </>
          ) : null}
        </Box>
      </Box>

      <Box mb={6} mt={5.5}>
        <TabsMetrics
          value={tab}
          setValue={(e) => setTab(e)}
        />
      </Box>
      <Grid container spacing={5}>
        {getMetric()}
      </Grid>
    </Box>
  );
}
export default StatsPage;
